import { addDays, type IsoDate } from './calendar';
import type { CashLedger } from './gstr3b';
import type { Paise } from './money';
import { type ByHead, byHead, sumHeads, TAX_HEADS } from './setoff';

/**
 * A GST challan (form PMT-06) paid by NEFT or RTGS (Phase 11). EkApp makes it through the GSP for what
 * GSTR-3B leaves to pay in cash, and the person sends the money from their bank to the Government's GST
 * account at RBI, with the challan's CPIN as the account number. Sources: rule 87 of the CGST Rules
 * (valid 15 days; the mandate form), the GST portal's own challan and mandate form (beneficiary "GST",
 * IFSC RBIS0GSTPMT, Reserve Bank of India), RBI's NEFT and RTGS FAQs (RTGS from ₹2 lakh).
 */

/** A challan can be paid for 15 days, the day it's made counting as the first (rule 87(2)). */
export const CHALLAN_VALID_DAYS = 15;

/** The Government's GST account at RBI: what every NEFT/RTGS challan's mandate form says to send it to. */
export const GST_BENEFICIARY = Object.freeze({
  name: 'GST',
  bank: 'Reserve Bank of India',
  ifsc: 'RBIS0GSTPMT',
});

/** RTGS takes ₹2 lakh or more (RBI); below that, NEFT. */
export const RTGS_MINIMUM: Paise = 2_00_000_00;

/** A challan's amounts by tax head and minor head, each in whole rupees (kept in paise, as everywhere). */
export interface ChallanAmounts {
  tax: ByHead;
  interest: ByHead;
  fee: ByHead;
  total: Paise;
}

const upToRupee = (amount: Paise) => Math.ceil(amount / 100) * 100;

/**
 * What a challan asks for, from what's left to pay in cash: the portal takes whole rupees, so each head
 * and minor head is rounded up (never short by a paisa); the extra stays in the cash ledger.
 */
export function challanAmounts(toPay: CashLedger): ChallanAmounts {
  const round = (heads: ByHead) =>
    byHead(Object.fromEntries(TAX_HEADS.map((h) => [h, upToRupee(Math.max(0, heads[h]))])));
  const tax = round(toPay.tax);
  const interest = round(toPay.interest);
  const fee = round(toPay.fee);
  return { tax, interest, fee, total: sumHeads(tax) + sumHeads(interest) + sumHeads(fee) };
}

/** By head, everything the challan pays for that head (tax, interest and fee together). */
export function challanByHead(amounts: ChallanAmounts): ByHead {
  return byHead(Object.fromEntries(TAX_HEADS.map((h) => [h, amounts.tax[h] + amounts.interest[h] + amounts.fee[h]])));
}

/** The last day a challan made on `madeOn` can be paid. */
export function challanLastDay(madeOn: IsoDate): IsoDate {
  return addDays(madeOn, CHALLAN_VALID_DAYS - 1);
}

/** A CPIN as a bank's form shows it best, in fours: "2609 2900 4512 18". */
export function spacedCpin(cpin: string): string {
  return cpin.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

/** NEFT or RTGS: RTGS only from ₹2 lakh. */
export function transferModes(total: Paise): ('neft' | 'rtgs')[] {
  return total >= RTGS_MINIMUM ? ['neft', 'rtgs'] : ['neft'];
}

/**
 * Where a challan stands, as EkApp acts on it: not paid yet; with the banks (sent, not confirmed);
 * paid (RBI credited the cash ledger); failed (the bank's NEFT failed or came back: it can be sent again
 * while the challan lasts); expired (past its last day, or cancelled: a new one is needed).
 */
export type ChallanState = 'unpaid' | 'pending' | 'paid' | 'failed' | 'expired';

/**
 * GSTN's challan status codes (the challan summary's `status`), as the portal's own payment pages read
 * them. GSTN publishes no table; the portal shows N Not paid, I Initiated, C Awaiting bank confirmation,
 * P Awaiting bank clearance, U No response from bank, S Paid, T Paid at tax office, F Failed,
 * K Failed for NEFT, D Dishonoured, X Expired, B Blocked (debited after it expired), R Cancelled. Some
 * answers spell it out ("Not Paid"). Anything unknown (Z) is treated as still with the banks.
 */
export function challanState(status: string): ChallanState {
  const s = status.trim().toUpperCase();
  switch (s) {
    case 'N':
    case 'NOT PAID':
      return 'unpaid';
    case 'S':
    case 'T':
    case 'PAID':
    case 'PAID AT TAX OFFICE':
      return 'paid';
    case 'F':
    case 'K':
    case 'D':
    case 'FAILED':
    case 'FAILED FOR NEFT':
    case 'DISHONOURED':
      return 'failed';
    case 'X':
    case 'B':
    case 'R':
    case 'EXPIRED':
    case 'BLOCKED':
    case 'CANCELLED':
      return 'expired';
    default:
      return 'pending';
  }
}

/** Indian English words for a whole-rupee amount, as a challan prints it: "Rupees Twenty One Thousand Seven Hundred Only". */
export function rupeesInWords(amount: Paise): string {
  const rupees = Math.floor(amount / 100);
  if (rupees === 0) return 'Rupees Zero Only';
  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const below100 = (n: number) => (n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`);
  const below1000 = (n: number) =>
    [n >= 100 ? `${ones[Math.floor(n / 100)]} Hundred` : '', n % 100 ? below100(n % 100) : '']
      .filter(Boolean)
      .join(' ');
  const parts: string[] = [];
  let rest = rupees;
  const crore = Math.floor(rest / 1_00_00_000);
  rest %= 1_00_00_000;
  if (crore)
    parts.push(
      `${crore >= 1000 ? rupeesInWords(crore * 100).replace(/^Rupees | Only$/g, '') : below1000(crore)} Crore`,
    );
  const lakh = Math.floor(rest / 1_00_000);
  rest %= 1_00_000;
  if (lakh) parts.push(`${below100(lakh)} Lakh`);
  const thousand = Math.floor(rest / 1000);
  rest %= 1000;
  if (thousand) parts.push(`${below100(thousand)} Thousand`);
  if (rest) parts.push(below1000(rest));
  return `Rupees ${parts.join(' ')} Only`;
}
